<script setup>
import { computed } from 'vue'

const props = defineProps({
  year: { type: Number, required: true },
  month: { type: Number, required: true }, // 1-12
  plannedMap: { type: Object, required: true }, // Map<YYYY-MM-DD, {id,is_active}>
  busy: { type: Boolean, default: false }
})
const emit = defineEmits(['toggle'])

const title = computed(() => `${props.year}年 ${props.month}月`)

const ymStr = computed(() => `${props.year}-${String(props.month).padStart(2,'0')}`)

const matrix = computed(() => {
  const first = new Date(props.year, props.month - 1, 1)
  const firstDow = first.getDay() // 日曜=0
  const daysInMonth = new Date(props.year, props.month, 0).getDate()
  const cells = []
  for (let i = 0; i < firstDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)
  while (cells.length < 42) cells.push(null)
  return cells
})

function toISO(day) {
  return `${ymStr.value}-${String(day).padStart(2,'0')}`
}

function isPlanned(day) {
  if (!day) return false
  const item = props.plannedMap.get(toISO(day))
  return !!(item && item.is_active)
}

// 過去 + 今日をブロック（当日はHELP、前日までが計画休・JST基準）
function isPastOrToday(day) {
  if (!day) return false
  const iso = toISO(day)
  // JSTの今日を取得（タイムゾーン依存を排除）
  const now = new Date()
  const j = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }))
  const t = `${j.getFullYear()}-${String(j.getMonth()+1).padStart(2,'0')}-${String(j.getDate()).padStart(2,'0')}`
  return iso <= t  // ← 今日もブロック（計画休は明日以降のみ・JST基準）
}

// 列から曜日を判定（0:日〜6:土）→ グリッドは常に7列なのでTZに依存しない
function colOf(idx) {
  return idx % 7
}

function isSunCol(idx) {
  return colOf(idx) === 0
}

function isSatCol(idx) {
  return colOf(idx) === 6
}

function onClick(day) {
  if (!day || props.busy || isPastOrToday(day)) return
  emit('toggle', toISO(day))
}
</script>

<template>
  <div class="month-card">
    <div class="month-title">{{ title }}</div>
    <div class="weekdays">
      <div class="wd sun">日</div>
      <div class="wd">月</div>
      <div class="wd">火</div>
      <div class="wd">水</div>
      <div class="wd">木</div>
      <div class="wd">金</div>
      <div class="wd sat">土</div>
    </div>
    <div class="grid">
      <button
        v-for="(cell, idx) in matrix" 
        :key="idx"
        class="cell"
        :class="{
          empty: !cell,
          planned: isPlanned(cell),
          blocked: isPastOrToday(cell),
          sun: cell && isSunCol(idx),
          sat: cell && isSatCol(idx)
        }"
        @click="onClick(cell)"
        :disabled="!cell || busy || isPastOrToday(cell)"
        :title="isPastOrToday(cell) && cell ? '当日以前は計画休NG（当日はHELPを使用）' : ''"
        :aria-label="cell
          ? `${props.month}月${cell}日 ${isSunCol(idx) ? '日曜' : isSatCol(idx) ? '土曜' : '平日'}${isPastOrToday(cell) ? '（計画休は不可）' : ''}`
          : ''"
      >
        <span v-if="cell">{{ cell }}</span>
      </button>
    </div>
    <div class="legend">
      <span class="dot sun"></span> 日曜
      <span class="dot sat"></span> 土曜
      <span class="dot planned"></span> 計画休暇
    </div>
  </div>
</template>

<style scoped>
/* 週末配色の変数定義（.month-card 内でのみ有効） */
.month-card {
  --sun-fg: #b91c1c;    /* 赤-700 */
  --sun-bg: #fee2e2;    /* 赤-100 */
  --sun-br: #fecaca;    /* 赤-200 */

  --sat-fg: #1d4ed8;    /* 青-700 */
  --sat-bg: #dbeafe;    /* 青-100 */
  --sat-br: #bfdbfe;    /* 青-200 */

  /* 計画休暇（紫系：赤/青と被らない） */
  --planned-fg: #6d28d9; /* violet-700 */
  --planned-bg: #ede9fe; /* violet-100 */
  --planned-br: #a78bfa; /* violet-300 */

  /* カードのスタイル */
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 12px 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.month-title {
  font-weight: 700;
  margin-bottom: 8px;
  color: #1f2937;
  text-align: center;
  position: sticky;
  top: 0;
  background: #fff;
  padding: 6px 0;
  z-index: 1;
  border-bottom: 1px solid #f3f4f6;
}

.weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 6px;
}

.wd {
  font-size: 0.85rem;
  color: #6b7280;
  text-align: center;
  font-weight: 600;
}

/* 曜日ヘッダの週末配色 */
.wd.sun {
  color: var(--sun-fg);
  font-weight: 700;
}

.wd.sat {
  color: var(--sat-fg);
  font-weight: 700;
}

.grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(36px, 1fr));
  gap: 6px;
}

.cell {
  /* スマホで十分なタップ領域を確保 */
  min-height: 44px;
  aspect-ratio: 1 / 1; /* 正方形：対応ブラウザで綺麗に並ぶ */
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 1rem; /* 入力時のズーム抑止（>=16px相当） */
}

.cell:hover:not(:disabled):not(.empty):not(.planned) {
  filter: brightness(0.97);
}

/* 週末セルの基本色（平常時） */
.cell.sun:not(.empty) {
  background: var(--sun-bg);
  border-color: var(--sun-br);
  color: var(--sun-fg);
}

.cell.sat:not(.empty) {
  background: var(--sat-bg);
  border-color: var(--sat-br);
  color: var(--sat-fg);
}

/* 色覚配慮: 週末セルに破線ボーダー（planned時は通常枠） */
.cell.sun:not(.planned) {
  border-style: dashed;
}

.cell.sat:not(.planned) {
  border-style: dashed;
}

.cell.empty {
  background: transparent;
  border: none;
  cursor: default;
}

/* ブロック状態（当日以前）は減光。週末色/平日色とも等しく適用 */
.cell.blocked {
  opacity: 0.55;
  cursor: not-allowed;
}

/* 計画休は最優先で視覚強調（週末色より上書き） */
.cell.planned {
  background: var(--planned-bg) !important;
  border-color: var(--planned-br) !important;
  color: var(--planned-fg) !important;
  font-weight: 700;
  border-style: solid !important;
  position: relative;
}

.cell:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.legend {
  margin-top: 8px;
  font-size: 0.85rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot.planned {
  background: var(--planned-bg);
  border: 1px solid var(--planned-br);
}

.dot.sun {
  background: var(--sun-br);
  border: 1px solid var(--sun-fg);
}

.dot.sat {
  background: var(--sat-br);
  border: 1px solid var(--sat-fg);
}
</style>

